import { useState, useEffect, useCallback, useRef } from "react";
import { message as antdMessage } from "antd";
import useSocket from "./useSocket";
import {
  fetchRequirements,
  fetchUsers,
  fetchUnreadCounts,
  createRequirement as apiCreateRequirement,
  moveRequirementColumn as apiMoveColumn,
  deleteRequirement as apiDeleteRequirement,
  fetchMessages as apiFetchMessages,
  sendMessage as apiSendMessage,
  deleteMessage as apiDeleteMessage,
  updateDescription as apiUpdateDescription,
} from "../crmApi";

export default function useCRM(currentUser) {
  // ── Board ─────────────────────────────────────────────
  const [columns, setColumns] = useState({
    requirement: [],
    in_discussion: [],
    business_conversion: [],
  });
  const [boardLoading, setBoardLoading] = useState(false);

  // ── Unread counts { [requirementId]: number } ─────────
  const [unreadCounts, setUnreadCounts] = useState({});

  // ── Users ─────────────────────────────────────────────
  const [users, setUsers] = useState([]);

  // ── Modal ─────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Drawer / conversation ─────────────────────────────
  const [drawerCard, setDrawerCard] = useState(null);
  const drawerCardRef = useRef(null); // always reflects latest drawerCard for socket handler

  // Keep ref in sync
  useEffect(() => {
    drawerCardRef.current = drawerCard;
  }, [drawerCard]);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  // ─────────────────────────────────────────────────────
  // Socket — live unread badge update
  // When a new_message event arrives for this user,
  // increment the unread count for that requirement card
  // ─────────────────────────────────────────────────────
  const handleSocketMessage = useCallback((payload) => {
    const { requirementId, message } = payload;

    const isDrawerOpen = drawerCardRef.current?._id === requirementId;

    if (isDrawerOpen) {
      // Drawer is open — push message in directly
      setMessages((prev) => {
        // Avoid duplicate if the sender's optimistic message already exists
        const alreadyExists = prev.some((m) => m._id === message._id);
        if (alreadyExists) return prev;
        return [
          ...prev,
          {
            ...message,
            createdAt: message.createdAt || new Date().toISOString(),
          },
        ];
      });
    } else {
      // Drawer is closed — increment unread badge
      console.log("[socket] incrementing unread for", requirementId);
      setUnreadCounts((prev) => ({
        ...prev,
        [requirementId]: (prev[requirementId] || 0) + 1,
      }));
    }
  }, []);

  const handleCrmEvent = useCallback((event, payload) => {
    console.log("[socket CRM event]", event, payload);
    switch (event) {
      case "card:created": {
        const { card } = payload;
        setColumns((prev) => {
          // Avoid duplicate
          const exists = prev.requirement.some((c) => c._id === card._id);
          if (exists) return prev;
          return {
            ...prev,
            requirement: [card, ...prev.requirement],
          };
        });
        break;
      }
      case "card:updated": {
        const { card } = payload;
        setColumns((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((col) => {
            next[col] = next[col].map((c) => (c._id === card._id ? { ...c, ...card } : c));
          });
          return next;
        });
        setDrawerCard((prev) =>
          prev?._id === card._id ? { ...prev, ...card } : prev
        );
        break;
      }
      case "card:moved": {
        const { cardId, toColumn, card } = payload;
        setColumns((prev) => {
          const next = {
            requirement: [...prev.requirement],
            in_discussion: [...prev.in_discussion],
            business_conversion: [...prev.business_conversion],
          };
          
          // First, remove the card from its current column if present
          let movedCard = null;
          Object.keys(next).forEach((col) => {
            next[col] = next[col].filter((c) => {
              if (c._id === cardId) {
                movedCard = c;
                return false;
              }
              return true;
            });
          });

          // Insert into target column
          const finalCard = movedCard ? { ...movedCard, ...card, column: toColumn } : { ...card, column: toColumn };
          
          // To avoid duplicates in target column:
          next[toColumn] = next[toColumn].filter((c) => c._id !== cardId);
          next[toColumn] = [finalCard, ...next[toColumn]];
          
          return next;
        });

        setDrawerCard((prev) =>
          prev?._id === cardId ? { ...prev, ...card, column: toColumn } : prev
        );
        break;
      }
      case "card:deleted": {
        const { cardId, column } = payload;
        setColumns((prev) => {
          const next = { ...prev };
          if (next[column]) {
            next[column] = next[column].filter((c) => c._id !== cardId);
          } else {
            // Fallback: search all columns
            Object.keys(next).forEach((col) => {
              next[col] = next[col].filter((c) => c._id !== cardId);
            });
          }
          return next;
        });
        setDrawerCard((prev) => (prev?._id === cardId ? null : prev));
        break;
      }
      default:
        break;
    }
  }, []);

  const { watchRequirement, unwatchRequirement, joinCrmBoard, leaveCrmBoard } = useSocket(
    currentUser?._id,
    handleSocketMessage,
    handleCrmEvent,
  );

  // ─────────────────────────────────────────────────────
  // Load board + users + unread counts on mount
  // ─────────────────────────────────────────────────────
  const loadBoard = useCallback(async () => {
    setBoardLoading(true);
    try {
      const data = await fetchRequirements();
      setColumns(data);
    } catch (err) {
      antdMessage.error(
        err.response?.data?.message || "Failed to load requirements",
      );
    } finally {
      setBoardLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch {
      // non-critical
    }
  }, []);

  const loadUnreadCounts = useCallback(async () => {
    try {
      const data = await fetchUnreadCounts();
      setUnreadCounts(data);
    } catch {
      // non-critical — badge just won't show
    }
  }, []);

  useEffect(() => {
    loadBoard();
    loadUsers();
    loadUnreadCounts();
  }, [loadBoard, loadUsers, loadUnreadCounts]);

  useEffect(() => {
    joinCrmBoard();
    return () => {
      leaveCrmBoard();
    };
  }, [joinCrmBoard, leaveCrmBoard]);

  // ─────────────────────────────────────────────────────
  // Move column — shared by drag-drop + dropdown + drawer
  // ─────────────────────────────────────────────────────
  const handleMoveColumn = useCallback(
    async (cardId, fromColumn, toColumn) => {
      if (fromColumn === toColumn) return;

      let movedCard = null;
      setColumns((prev) => {
        const next = {
          requirement: [...prev.requirement],
          in_discussion: [...prev.in_discussion],
          business_conversion: [...prev.business_conversion],
        };
        next[fromColumn] = prev[fromColumn].filter((c) => {
          if (c._id === cardId) {
            movedCard = c;
            return false;
          }
          return true;
        });
        if (movedCard) {
          next[toColumn] = [
            { ...movedCard, column: toColumn },
            ...next[toColumn],
          ];
        }
        return next;
      });

      setDrawerCard((prev) =>
        prev?._id === cardId ? { ...prev, column: toColumn } : prev,
      );

      try {
        await apiMoveColumn(cardId, toColumn);
      } catch {
        antdMessage.error("Failed to move card — reverting");
        loadBoard();
      }
    },
    [loadBoard],
  );

  // ─────────────────────────────────────────────────────
  // Drag-and-drop
  // ─────────────────────────────────────────────────────
  const handleDragEnd = useCallback(
    (result) => {
      const { draggableId, source, destination } = result;

      if (!destination) return;
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      )
        return;

      const fromColumn = source.droppableId;
      const toColumn = destination.droppableId;

      if (fromColumn === toColumn) {
        setColumns((prev) => {
          const reordered = [...prev[fromColumn]];
          const [removed] = reordered.splice(source.index, 1);
          reordered.splice(destination.index, 0, removed);
          return { ...prev, [fromColumn]: reordered };
        });
        return;
      }

      // Cross-column — insert at exact drop position
      let movedCard = null;
      setColumns((prev) => {
        const next = {
          requirement: [...prev.requirement],
          in_discussion: [...prev.in_discussion],
          business_conversion: [...prev.business_conversion],
        };
        next[fromColumn] = prev[fromColumn].filter((c) => {
          if (c._id === draggableId) {
            movedCard = c;
            return false;
          }
          return true;
        });
        if (movedCard) {
          next[toColumn] = [...prev[toColumn]];
          next[toColumn].splice(destination.index, 0, {
            ...movedCard,
            column: toColumn,
          });
        }
        return next;
      });

      setDrawerCard((prev) =>
        prev?._id === draggableId ? { ...prev, column: toColumn } : prev,
      );

      apiMoveColumn(draggableId, toColumn).catch(() => {
        antdMessage.error("Failed to move card — reverting");
        loadBoard();
      });
    },
    [loadBoard],
  );

  // ─────────────────────────────────────────────────────
  // Create requirement
  // ─────────────────────────────────────────────────────
  const handleCreateRequirement = async (values) => {
    setSubmitting(true);
    try {
      const newCard = await apiCreateRequirement({
        jobName: values.jobName,
        region: values.region,
        projectValue: values.projectValue,
        duration: values.duration,
        hourlyRate: values.hourlyRate || null,
        assignedTo: values.assignedTo,
        description: values.description || "",
        companyName: values.companyName || "",
        sourceLink: values.sourceLink || "",
        spocName: values.spocName || "",
        spocEmail: values.spocEmail || "",
        spocNumber: values.spocNumber || "",
      });
      setColumns((prev) => ({
        ...prev,
        requirement: [newCard, ...prev.requirement],
      }));
      setModalOpen(false);
      antdMessage.success("Requirement created");
    } catch (err) {
      antdMessage.error(
        err.response?.data?.message || "Failed to create requirement",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────
  // Delete requirement
  // ─────────────────────────────────────────────────────
  const handleDeleteRequirement = async (cardId, column) => {
    setColumns((prev) => ({
      ...prev,
      [column]: prev[column].filter((c) => c._id !== cardId),
    }));
    try {
      await apiDeleteRequirement(cardId);
      antdMessage.success("Requirement deleted");
      if (drawerCard?._id === cardId) setDrawerCard(null);
    } catch {
      antdMessage.error("Failed to delete");
      loadBoard();
    }
  };

  // ─────────────────────────────────────────────────────
  // Open drawer — fetch messages + mark as read
  // ─────────────────────────────────────────────────────
  const openDrawer = async (card) => {
    setDrawerCard(card);
    setMessages([]);
    setMessagesLoading(true);

    // Join the requirement's socket room for live messages
    watchRequirement(card._id);

    // Clear unread badge immediately
    setUnreadCounts((prev) => {
      const next = { ...prev };
      delete next[card._id];
      return next;
    });

    try {
      const result = await apiFetchMessages(card._id);
      setMessages(result);
    } catch {
      antdMessage.error("Failed to load messages");
    } finally {
      setMessagesLoading(false);
    }
  };

  const closeDrawer = () => {
    // Leave the requirement's socket room
    if (drawerCard) unwatchRequirement(drawerCard._id);
    setDrawerCard(null);
    setMessages([]);
  };

  // ─────────────────────────────────────────────────────
  // Send message
  // ─────────────────────────────────────────────────────
  const handleSendMessage = async (text) => {
    if (!drawerCard || !text.trim()) return;
    setSendingMessage(true);

    // const optimisticMsg = {
    //   _id: `temp-${Date.now()}`,
    //   author: currentUser,
    //   text: text.trim(),
    //   createdAt: new Date().toISOString(),
    //   isOptimistic: true,
    // };
    // setMessages((prev) => [...prev, optimisticMsg]);

    try {
      const saved = await apiSendMessage(drawerCard._id, text.trim());
      // setMessages((prev) => [...prev, saved]);
      // setMessages((prev) =>
      //   prev.map((m) => (m._id === optimisticMsg._id ? saved : m)),
      // );
    } catch {
      // setMessages((prev) => prev.filter((m) => m._id !== optimisticMsg._id));
      antdMessage.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // ─────────────────────────────────────────────────────
  // Delete message
  // ─────────────────────────────────────────────────────
  const handleDeleteMessage = async (messageId) => {
    setMessages((prev) => prev.filter((m) => m._id !== messageId));
    try {
      await apiDeleteMessage(drawerCard._id, messageId);
    } catch {
      antdMessage.error("Failed to delete message");
      const result = await apiFetchMessages(drawerCard._id);
      setMessages(result.data);
    }
  };

  // ─────────────────────────────────────────────────────
  // Update description
  // ─────────────────────────────────────────────────────
  const handleUpdateDescription = async (cardId, description) => {
    try {
      await apiUpdateDescription(cardId, description);
      setColumns((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((col) => {
          next[col] = next[col].map((c) =>
            c._id === cardId ? { ...c, description } : c,
          );
        });
        return next;
      });
      setDrawerCard((prev) =>
        prev?._id === cardId ? { ...prev, description } : prev,
      );
      antdMessage.success("Description saved");
    } catch {
      antdMessage.error("Failed to save description");
      throw new Error("Failed");
    }
  };

  return {
    // board
    columns,
    boardLoading,
    // unread
    unreadCounts,
    // users
    users,
    // modal
    modalOpen,
    setModalOpen,
    submitting,
    handleCreateRequirement,
    // move
    handleDragEnd,
    handleMoveColumn,
    handleDeleteRequirement,
    // drawer
    drawerCard,
    openDrawer,
    closeDrawer,
    // messages
    messages,
    messagesLoading,
    sendingMessage,
    handleSendMessage,
    handleDeleteMessage,
    handleUpdateDescription,
  };
}
